import { type FC, useState } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { UiCheckCircleIcon } from "@jti/brand-icons/ui-check-circle";
import { Button } from "@jti/core-components/components/Button";
import { SupportingText } from "@jti/core-components/components/SupportingText";

import { CopyCodeButton } from "../CopyCodeButton";

import type { ReferralPersonalCodeIconPropsType } from "./ReferralPersonalCodeIcon";
import { ReferralPersonalCodeIcon } from "./ReferralPersonalCodeIcon";

import styles from "./ReferralPersonalCode.module.scss";

export interface ReferralPersonalCodeBlocksPropsType {
  code: string;
  infoTitle?: string;
  shareTitle: string;
  items: ReferralPersonalCodeIconPropsType[];
  shareButtonLabel?: string;
  shareCodeVariant?: string;
  onShareClick?: (props?: { skipNative?: boolean }) => void | Promise<void>;
}
const ReferralPersonalCodeShareIcons: FC<{ items: ReferralPersonalCodeIconPropsType[] }> = ({ items }) => {
  if (!items?.length) {
    return null;
  }

  return (
    <>
      {items.map((element: ReferralPersonalCodeIconPropsType) => (
        <ReferralPersonalCodeIcon
          key={element.url}
          img={element.img}
          url={element.url}
          trackingName={element.trackingName}
        />
      ))}
    </>
  );
};
export const ReferralPersonalCodeBlocks: FC<ReferralPersonalCodeBlocksPropsType> = ({
  code,
  infoTitle,
  shareTitle,
  items,
  shareButtonLabel,
  onShareClick,
  shareCodeVariant = "shareIcons",
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const { __ } = useTranslations();

  const copyCodeButton = (
    <CopyCodeButton value={code} data-testid="referral-personal-code__copy" onCopy={() => setIsCopied(true)} />
  );

  const blocksContent =
    shareCodeVariant === "shareButton" ? (
      <>
        <div className={styles.codeBlock}>{copyCodeButton}</div>
        <Button
          className={styles.shareButton}
          variant="primary"
          onClick={() => onShareClick?.()}
          data-testid="referral-personal-code__share"
        >
          {shareButtonLabel}
        </Button>
        <Button
          className={classNames(styles.shareButton, styles.largeOnly)}
          variant="primary"
          onClick={() => onShareClick?.({ skipNative: true })}
          data-testid="referral-personal-code__share-skip-native"
        >
          {shareButtonLabel}
        </Button>
      </>
    ) : (
      <>
        <div className={classNames(styles.singleBlock, styles.leftBlock)}>{copyCodeButton}</div>
        <div className={classNames(styles.singleBlock, styles.rightBlock)}>
          <div className={styles.shareTitle}>{shareTitle}</div>
          <div className={styles.socialRow} data-testid={"social-media-block"}>
            <ReferralPersonalCodeShareIcons items={items} />
          </div>
          {infoTitle && (
            <div className={styles.info}>
              <SupportingText data-testid={"your-personal-code-info"}>{infoTitle}</SupportingText>
            </div>
          )}
        </div>
      </>
    );

  return (
    <>
      <div className={styles.blocks}>{blocksContent}</div>

      {isCopied && (
        <div className={styles.copiedText} data-testid="copied-text">
          <UiCheckCircleIcon className={styles.copyIcon} />
          {__("copy.code.button")}
        </div>
      )}
    </>
  );
};
